import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { getRoleBadgeDisplay, ROLE_FLAG_LABELS, ClientUserRole } from '@zerops/models/client-user';

type RoleBadgeColor = 'admin' | 'maintainer' | 'basic-user' | 'read-only' | 'developer' | 'guest';

@Component({
  selector: 'zui-role-badge',
  standalone: true,
  imports: [ CommonModule, MatTooltipModule ],
  templateUrl: './role-badge.component.html',
  styleUrl: './role-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoleBadgeComponent {
  roleCode = input.required<string>();
  canCreateProjects = input<boolean>();
  canViewFinances = input<boolean>(false);
  canEditFinances = input<boolean>(false);
  labelOverride = input<string>();
  colorOverride = input<RoleBadgeColor>();

  #display = computed(() => getRoleBadgeDisplay({
    roleCode: this.roleCode(),
    canCreateProjects: this.canCreateProjects(),
    canViewFinances: this.canViewFinances(),
    canEditFinances: this.canEditFinances()
  }));

  roleLabel = computed(() => this.labelOverride() || this.#display().label);

  colorVariant = computed((): RoleBadgeColor | null => {
    const override = this.colorOverride();
    if (override) return override;

    switch (this.roleCode()) {
      case ClientUserRole.Owner:
        return 'admin';

      case ClientUserRole.Admin:
        return 'maintainer';

      case ClientUserRole.BasicUser:
        return 'basic-user';

      case ClientUserRole.ReadOnly:
        return 'read-only';

      // Tracks the label, which is Developer or Guest by the same flag.
      case ClientUserRole.NoAccess:
        return this.canCreateProjects() ? 'developer' : 'guest';

      default:
        return null;
    }
  });

  isAdminColor = computed(() => this.colorVariant() === 'admin');
  isMaintainerColor = computed(() => this.colorVariant() === 'maintainer');
  isBasicUserColor = computed(() => this.colorVariant() === 'basic-user');
  isReadOnlyColor = computed(() => this.colorVariant() === 'read-only');
  isDeveloperColor = computed(() => this.colorVariant() === 'developer');
  isGuestColor = computed(() => this.colorVariant() === 'guest');

  hasExtraAccess = computed(() => this.#display().extraFlags.length > 0);

  extraAccessTooltip = computed(() =>
    this.#display().extraFlags.map((flag) => ROLE_FLAG_LABELS[flag]).join(', ')
  );
}
